import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import {
	BULIN_47_SCENE_PATH,
	type Bulin47QualityId,
	type Bulin47TemplateId,
} from "./bulin-47-preset";

/**
 * /bulin-47-ai-video-generator 样片区的片子，做法同 ai-kiss-showcase.ts。
 *
 * Examples 优先用竞品样片：media.io 两条（无水印、带原声，2026-10-15 抓取）onSite false，不进 VideoObject，
 * 卡上不标来源和模型。自产片在本页 hero 用预设出，onSite true，标档位，「Try this example」回填原图。
 * 来源和压制参数见 packages/scripts/bulin-47-showcase/README.md。
 *
 * R2 `home/showcase/bulin-47/` 下 clips/{id}.mp4、posters/{id}.jpg、refs/{ref}.jpg。
 * 清单为空时样片区整个不渲染。
 */
export interface Bulin47ShowcaseClip {
	/** 标题和一句话说明在 bulin47.showcase.items.{id}（各语种各一份），JSON-LD 与 <video> aria-label 也用它 */
	id: string;
	/** 出这条片用的画质档（只有自产片有），拼贴 hover 时显示档位名 */
	quality?: Bulin47QualityId;
	/** 出这条片用的模板；「Try this example」顺带切过去，狗照片才不会套人像模板 */
	template?: Bulin47TemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 图 1 + 默认场景图，「Try this example」填进 hero 的两个槽 */
	inputImages?: string[];
	/** 外部片的来源（只记档案，页面上不显示） */
	source?: string;
}

const R2_BASE = "home/showcase/bulin-47";

/**
 * 视频和 poster 地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件，上传前被请求过的连 404 也会缓存一天；换片子时把这个数字加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<Bulin47ShowcaseClip, "videoUrl" | "poster" | "inputImages"> & {
	/** 自产片的图 1（refs/{ref}.jpg），图 2 固定是默认场景图 */
	ref?: string;
};

/**
 * 顺序即拼贴顺序（一行排开），自产片排第 1、3、5 条。自产片是本页 HD（MiniMax H3 720p，10 秒）出的，
 * 服务端已套原声；外部片 2026-10-15 抓自 media.io/ai/explore/zone/bulin-47-rap-ai-trend 的
 * rap / rap_video 两个模板样片
 */
const SPECS: Spec[] = [
	{
		id: "lime-jacket",
		quality: "hd",
		template: "me",
		orientation: "portrait",
		onSite: true,
		duration: 10,
		hasAudio: true,
		ref: "woman-1",
	},
	{
		id: "mediaio-hoodie",
		orientation: "portrait",
		onSite: false,
		duration: 17,
		hasAudio: true,
		source: "media.io (Bulin 47 Rap AI Trend, rap)",
	},
	{
		id: "shiba",
		quality: "hd",
		template: "pet",
		orientation: "portrait",
		onSite: true,
		duration: 10,
		hasAudio: true,
		ref: "shiba-1",
	},
	{
		id: "mediaio-chimp",
		orientation: "portrait",
		onSite: false,
		duration: 17,
		hasAudio: true,
		source: "media.io (Bulin 47 Rap AI Trend, rap_video)",
	},
	{
		id: "teal-jacket",
		quality: "hd",
		template: "me",
		orientation: "portrait",
		onSite: true,
		duration: 10,
		hasAudio: true,
		ref: "man-1",
	},
];

export const BULIN_47_SCENE_URL = buildR2AssetsImageUrl(BULIN_47_SCENE_PATH);

export const bulin47ShowcaseClips: Bulin47ShowcaseClip[] = SPECS.map(
	({ ref, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: ref
			? [
					buildR2AssetsImageUrl(`${R2_BASE}/refs/${ref}.jpg`),
					BULIN_47_SCENE_URL,
				]
			: undefined,
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const BULIN_47_SHOWCASE_UPLOADED_AT = "2026-10-15T00:00:00+08:00";
